import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Fonts from '../../../../../ui/global/Fonts';
import Colors from '../../../../../ui/global/Colors';
import AITool from './AITool';
import Markdown from './Markdown';
import { defaultConfig } from '../../../../../utils/config';
import { getUIColors, resolveAgentKeyFromTool } from '../helpers/subAgent';
import type { AIToolData } from '../helpers/Types';
import { useStreamUIState } from '../helpers/streamStore';

const MAX_COLUMNS_WITH_CONTENT = 3;
const MAX_COLUMNS_WITHOUT_CONTENT = 3;

interface StreamViewProps {
  isExpanded?: boolean;
}

const GameStream: React.FC<StreamViewProps> = ({ isExpanded = true }) => {
  const { content, columnOrder, activeRoundKeys, activeRoundCompletedKeys, turnColumnKeys, taskToolIdByKey, toolVmById } = useStreamUIState() as any;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!defaultConfig.STREAM_AUTO_SCROLL) return;
    if (ref.current && content.blocks.length > 0) {
      const scrollElement = ref.current;
      requestAnimationFrame(() => {
        scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [content]);

  if (!isExpanded) return null;

  // Helper: derive agentKey for a tool block to place in a column
  const getAgentKeyForTool = (tool: AIToolData): string | undefined => resolveAgentKeyFromTool(tool);

  // Determine if a key corresponds to a known sub-agent (column) key for this turn/session
  const isSubAgentKey = (k?: string): boolean => {
    if (!k) return false;
    try {
      const order = Array.isArray(turnColumnKeys) && turnColumnKeys.length > 0
        ? turnColumnKeys
        : (Array.isArray(columnOrder) ? columnOrder : []);
      const active = Array.isArray(activeRoundKeys) ? activeRoundKeys : [];
      const byTask: Record<string, string> = (taskToolIdByKey || {}) as any;
      const keys = new Set<string>();
      for (const v of order) keys.add(String(v));
      for (const v of active) keys.add(String(v));
      for (const v of Object.keys(byTask)) keys.add(String(v));
      return keys.has(String(k));
    } catch (_) {
      return false;
    }
  };

  // Helper for sectioned columns layout - only classify as column content if it is a sub-agent key
  const getBlockKey = (b: any): string | undefined => {
    if (!b) return undefined;
    if (b.type === 'text') {
      const k = (b as any).agentKey as string | undefined;
      return isSubAgentKey(k) ? k : undefined;
    }
    if (b.type === 'tool') {
      const k = getAgentKeyForTool(b.content as AIToolData);
      return isSubAgentKey(k) ? k : undefined;
    }
    return undefined;
  };

  // If parallel sub-agents are enabled AND sub-agent content is visible, render sections
  // (main/full-width and per-turn columns). Otherwise, fall back to a simple vertical list.
  if (defaultConfig.STREAM_PARALLEL_SUBAGENTS && defaultConfig.SHOW_SUB_AGENT_CONTENT) {
    const blocks = content.blocks;

    type Section = { type: 'main'; blocks: any[] } | { type: 'columns'; blocks: any[]; keys: string[] };
    const sections: Section[] = [];

    const isSectionComplete = (keys: string[]): boolean => {
      if (!keys || keys.length === 0) return true;
      const activeKeys = Array.isArray(activeRoundKeys) ? activeRoundKeys.map(String) : [];
      const activeCompleted = Array.isArray(activeRoundCompletedKeys) ? activeRoundCompletedKeys.map(String) : [];
      if (activeKeys.length > 0) {
        // During an active round, require all keys in this section that are part of the round to be marked complete in this round
        for (const k of keys) {
          const inRound = activeKeys.includes(String(k));
          if (inRound && !activeCompleted.includes(String(k))) return false;
        }
        return true;
      }
      // Fallback: no active round tracked; use tool VM completion status
      for (const k of keys) {
        const toolId = (taskToolIdByKey || {})[String(k)];
        const vm = toolId ? (toolVmById || {})[String(toolId)] : undefined;
        if (!vm || (vm as any).status !== 'complete') return false;
      }
      return true;
    };

    type MutableCols = { type: 'columns'; blocks: any[]; keys: Set<string>; tailMain: any[] };
    let current: MutableCols | null = null;
    let pendingMain: any[] = [];

    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      const key = getBlockKey(b);
      if (key) {
        // Flush any pending main section before starting/continuing columns
        if (!current && pendingMain.length > 0) {
          sections.push({ type: 'main', blocks: pendingMain });
          pendingMain = [];
        }
        if (!current) {
          current = { type: 'columns', blocks: [], keys: new Set<string>(), tailMain: [] };
        }
        current.keys.add(String(key));
        current.blocks.push(b);
      } else {
        if (current) {
          const done = isSectionComplete(Array.from(current.keys));
          if (!done) {
            // Keep main-agent content out of the columns; render after the columns in this section
            current.tailMain.push(b);
          } else {
            // Round complete; flush columns section and then flush this and any buffered main
            sections.push({ type: 'columns', blocks: current.blocks, keys: Array.from(current.keys) });
            if (current.tailMain.length > 0) sections.push({ type: 'main', blocks: current.tailMain });
            sections.push({ type: 'main', blocks: [b] });
            current = null;
          }
        } else {
          // No active columns; accumulate contiguous main blocks
          pendingMain.push(b);
        }
      }
    }

    // Flush trailing sections
    if (current) {
      sections.push({ type: 'columns', blocks: current.blocks, keys: Array.from(current.keys) });
      if (current.tailMain.length > 0) sections.push({ type: 'main', blocks: current.tailMain });
      current = null;
    }
    if (pendingMain.length > 0) {
      sections.push({ type: 'main', blocks: pendingMain });
      pendingMain = [];
    }

    const indexOfKey = (k: string): number => {
      const order = Array.isArray(turnColumnKeys) && turnColumnKeys.length > 0 ? turnColumnKeys : (Array.isArray(columnOrder) ? columnOrder : []);
      const idx = order.indexOf(String(k));
      return idx === -1 ? 9999 : idx;
    };

    // Group consecutive text blocks for a given sub-agent key into a single item.
    // Tools for the same key act as separators. Tools named 'task' are ignored here.
    const groupColumnItems = (
      colBlocks: any[],
      key: string,
      showText: boolean
    ): Array<{ kind: 'text'; id: any; content: string } | { kind: 'tool'; id: any; tool: AIToolData }> => {
      const items: Array<{ kind: 'text'; id: any; content: string } | { kind: 'tool'; id: any; tool: AIToolData }> = [];
      for (const b of colBlocks) {
        const bKey = getBlockKey(b);
        if (String(bKey || '') !== String(key)) continue;

        if (b.type === 'tool') {
          const tool = b.content as AIToolData;
          if ((tool as any)?.name === 'task') continue;
          items.push({ kind: 'tool', id: b.id, tool });
          continue;
        }

        if (b.type === 'text') {
          if (!showText) continue;
          const contentStr = String((b as any).content ?? '');
          const last = items[items.length - 1];
          if (last && last.kind === 'text') {
            last.content += (last.content.endsWith('\n') ? '' : '\n') + contentStr;
          } else {
            items.push({ kind: 'text', id: b.id, content: contentStr });
          }
        }
      }
      return items;
    };

    return (
      <StreamContentArea ref={ref}>
        {sections.map((section, sIdx) => {
          if (section.type === 'main') {
            return (
              <React.Fragment key={`sec_main_${sIdx}`}>
                {section.blocks.map((block: any, idx: number) => {
                  if (block.type === 'text') {
                    const agentKey = (block as any).agentKey as string | undefined;
                    if (!defaultConfig.SHOW_SUB_AGENT_CONTENT && isSubAgentKey(agentKey)) return null;
                    return (
                      <TextBlock key={block.id || `main_${sIdx}_${idx}`}>
                        <Markdown content={block.content as string} />
                      </TextBlock>
                    );
                  }
                  if (block.type === 'tool') {
                    // Ensure task tool is not shown outside the header
                    if (((block.content as any)?.name) === 'task') return null;
                    return (
                      <ToolBlock key={block.id || `main_${sIdx}_${idx}`}>
                        <AITool tool={block.content as AIToolData} />
                      </ToolBlock>
                    );
                  }
                  return null;
                })}
              </React.Fragment>
            );
          }
          // columns section (one turn; remains open until all keys' task tools are complete)
          const gridKeys = [...section.keys].sort((a, b) => indexOfKey(String(a)) - indexOfKey(String(b)));
          // Keep a consistent column count across all rows in this section
          const maxCols = defaultConfig.SHOW_SUB_AGENT_CONTENT ? MAX_COLUMNS_WITH_CONTENT : MAX_COLUMNS_WITHOUT_CONTENT;
          const cols = Math.min(maxCols, Math.max(1, gridKeys.length));
          const rowKeyGroups: (string | null)[][] = [];
          for (let i = 0; i < gridKeys.length; i += cols) {
            const group: (string | null)[] = gridKeys.slice(i, i + cols);
            // Pad final row with empty columns so widths stay balanced
            while (group.length < cols) group.push(null);
            rowKeyGroups.push(group);
          }
          return (
            <React.Fragment key={`sec_cols_${sIdx}`}>
              {rowKeyGroups.map((rowKeys, rIdx) => (
                <React.Fragment key={`sec_cols_${sIdx}_row_${rIdx}`}>
                  {/* Header row for this row: show task VM per key if present in this section */}
                  <SegmentGrid $cols={cols}>
                    {rowKeys.map((k, cIdx) => {
                      if (!k) {
                        return (
                          <SegmentCol key={`header_col_${sIdx}_${rIdx}_empty_${cIdx}`} />
                        );
                      }
                      const headerVm = (() => {
                        try {
                          const toolId = (taskToolIdByKey || {})[String(k)];
                          const vm = toolId ? (toolVmById || {})[String(toolId)] : undefined;
                          return vm as AIToolData | undefined;
                        } catch (_) {
                          return undefined;
                        }
                      })();
                      const { bg, border, fg } = defaultConfig.STREAM_SUB_AGENT_COLORS ? getUIColors(k) : ({} as any);
                      return (
                        <SegmentCol key={`header_col_${sIdx}_${rIdx}_${k}`}>
                          {headerVm ? (
                            <ToolBlock>
                              <AITool tool={headerVm} />
                            </ToolBlock>
                          ) : (
                            defaultConfig.DEBUG_STREAM_TOOLS ? (
                              <TextBlock $bg={bg} $fg={fg} $border={border}>
                                {/* placeholder to keep header height consistent even before task arrives */}
                              </TextBlock>
                            ) : null
                          )}
                        </SegmentCol>
                      );
                    })}
                  </SegmentGrid>
                  {/* Columns content for this row */}
                  <SegmentGrid $cols={cols}>
                    {rowKeys.map((k, cIdx) => {
                      if (!k) {
                        return <SegmentCol key={`col_${sIdx}_${rIdx}_empty_${cIdx}`} />;
                      }
                      const items = groupColumnItems(section.blocks, k, defaultConfig.SHOW_SUB_AGENT_CONTENT);
                      const { bg, border, fg } = defaultConfig.STREAM_SUB_AGENT_COLORS ? getUIColors(k) : ({} as any);
                      return (
                        <SegmentCol key={`col_${sIdx}_${rIdx}_${k}`}>
                          {items.map((item, i) => {
                            if (item.kind === 'text') {
                              return (
                                <TextBlock key={`${item.id}_${i}`} $bg={bg} $fg={fg} $border={border}>
                                  <Markdown content={item.content} />
                                </TextBlock>
                              );
                            }
                            return (
                              <ToolBlock key={`${item.id}_${i}`}>
                                <AITool tool={(item as any).tool as AIToolData} />
                              </ToolBlock>
                            );
                          })}
                        </SegmentCol>
                      );
                    })}
                  </SegmentGrid>
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </StreamContentArea>
    );
  }

  // Fallback: original behavior when parallel sub-agents disabled
  // Render as a simple vertical list of blocks (no columns)
  {
    // Build a linear list with merged consecutive text blocks for the same sub-agent key.
    type ListItem =
      | { kind: 'text'; id: any; content: string; agentKey?: string }
      | { kind: 'tool'; id: any; tool: AIToolData };

    const buildListItems = (blocks: any[], showSubAgentText: boolean): ListItem[] => {
      const items: ListItem[] = [];
      for (const b of blocks) {
        if (b.type === 'tool') {
          items.push({ kind: 'tool', id: b.id, tool: b.content as AIToolData });
          continue;
        }
        if (b.type === 'text') {
          const agentKey = (b as any).agentKey as string | undefined;
          if (!showSubAgentText && isSubAgentKey(agentKey)) continue;
          const contentStr = String((b as any).content ?? '');
          const last = items[items.length - 1];
          if (last && last.kind === 'text' && (last as any).agentKey === agentKey) {
            (last as any).content += ((last as any).content.endsWith('\n') ? '' : '\n') + contentStr;
          } else {
            items.push({ kind: 'text', id: b.id, content: contentStr, agentKey });
          }
          continue;
        }
      }
      return items;
    };

    const items = buildListItems(content.blocks, defaultConfig.SHOW_SUB_AGENT_CONTENT);
    return (
      <StreamContentArea ref={ref}>
        {items.map((item, idx) => {
          if (item.kind === 'text') {
            const agentKey = (item as any).agentKey as string | undefined;
            const { bg, border, fg } = defaultConfig.STREAM_SUB_AGENT_COLORS ? getUIColors(agentKey) : ({} as any);
            return (
              <TextBlock key={`${item.id ?? idx}_${idx}`} $bg={bg} $fg={fg} $border={border}>
                <Markdown content={(item as any).content} />
              </TextBlock>
            );
          }
          return (
            <ToolBlock key={`${item.id ?? idx}_${idx}`}>
              <AITool tool={(item as any).tool as AIToolData} />
            </ToolBlock>
          );
        })}
      </StreamContentArea>
    );
  }
};

export default GameStream;

const StreamContentArea = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: scroll;
  gap: 0;

  padding-right: 16px;
  margin-right: -12px;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`;

const TextBlock = styled.div<{ $bg?: string; $fg?: string; $border?: string }>`
  flex: 0 0 auto;
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 14px;
  line-height: 1.4em;
  color: ${({ $fg }) => $fg ?? Colors.grey2};
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  margin-top: 5px;
  background: transparent;
  border-left: ${({ $border }) => $border ? `3px solid ${$border}` : 'none'};
  padding-left: ${({ $border }) => $border ? '8px' : '0'};
  &:first-child {
    margin-top: 0;
  }
`;

const ToolBlock = styled.div`
  flex: 0 0 auto;
  margin: 0;
  margin-top: 5px;
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  &:first-child {
    margin-top: 0;
  }
  & + & {
    margin-top: 0;
  }
  & * {
    min-width: 0;
  }
  pre,
  code {
    white-space: pre-wrap;
    word-break: break-word;
    overflow-x: hidden;
    max-width: 100%;
  }
  img,
  svg,
  canvas,
  iframe,
  table {
    max-width: 100%;
    height: auto;
  }
  [data-truncate],
  [data-ellipsize],
  .truncate,
  .pill,
  .detail-pill {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: clip;
    min-width: 0;
    max-width: 100%;
    display: inline-block;
  }
`;

const SegmentGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => Math.max(1, $cols)}, minmax(0, 1fr));
  gap: 12px;
  margin-top: 5px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(${({ $cols }) => Math.min(2, Math.max(1, $cols))}, minmax(0, 1fr));
  }
`;

const SegmentCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0; /* allow grid items to shrink so tracks stay equal */
  overflow: hidden;
`;
